<template>
  <div class="mini-overlay" tabindex="0" ref="root">
    <div class="mini-card puzzle-card" @keydown.esc="close">
      <div class="mini-header">
        <div>Puzzle</div>
      </div>

      <div
        class="puzzle-area"
        :style="{ width: width + 'px', height: height + 'px', '--cols': cols, '--rows': rows }"
      >
        <div
          v-for="(pieceId, slotIndex) in positions"
          :key="slotIndex"
          class="slot"
          :data-slot="slotIndex"
          @dragover.prevent
          @drop="onDrop($event, slotIndex)"
          :class="{ solved: pieceId === slotIndex }"
        >
          <div
            v-if="pieceId !== null"
            class="piece"
            draggable="true"
            @dragstart="onDragStart($event, pieceId)"
            :style="pieceStyle(pieceId)"
            :aria-label="'Puzzle piece ' + (pieceId+1)"
          ></div>
        </div>

        <!-- vollständiges Bild als Overlay, sichtbar wenn revealed === true -->
        <div v-if="revealed" class="full-overlay" :style="fullOverlayStyle"></div>
      </div>

      <div class="mini-footer">
        Ziehe ein Teil auf ein anderes, um die Plätze zu tauschen. Wenn alle Teile korrekt sind, wird das Spiel beendet.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PuzzleGame',
  emits: ['done', 'close'],
  props: {
    // if imageKey is provided (string filename or number index) it will be used, otherwise random bg is chosen
    imageKey: { type: [String, Number], default: null },
    cols: { type: Number, default: 6 },
    rows: { type: Number, default: 3 },
    piecesCount: { type: Number, default: 20 },
    width: { type: Number, default: 800 },
    height: { type: Number, default: 600 }
  },
  data() {
    return {
      positions: [], // positions[slotIndex] = pieceId
      dragPieceId: null,
      bgUrl: '', // chosen background image URL
      revealed: false,
      revealTimeoutId: null
    };
  },
  mounted() {
    this.initPositions();
    this.chooseBackground();
    this.$refs.root && this.$refs.root.focus();
  },
  beforeUnmount() {
    this.clearRevealTimer();
  },
  methods: {
    clearRevealTimer() {
      if (this.revealTimeoutId) {
        clearTimeout(this.revealTimeoutId);
        this.revealTimeoutId = null;
      }
    },

    // choose random bg from src/assets/bg or use imageKey if provided
    chooseBackground() {
      // try explicit imageKey first
      if (this.imageKey !== null && this.imageKey !== undefined) {
        try {
          // if numeric, try index file name like "0.png"
          if (typeof this.imageKey === 'number') {
            this.bgUrl = require(`@/assets/bg/${this.imageKey}.png`);
            return;
          }
          // if string, try that filename (with or without extension)
          const name = String(this.imageKey);
          try { this.bgUrl = require(`@/assets/bg/${name}.png`); return; } catch (e) {console.warn(e);}
          try { this.bgUrl = require(`@/assets/bg/${name}.jpg`); return; } catch (e) {console.warn(e);}
        } catch (e) {
          // fallthrough to random selection
        }
      }

      // webpack require.context to collect available bg images
      try {
        // relative path from this file to src/assets/bg -> ../assets/bg
        const ctx = require.context('../assets/bg', false, /\.(png|jpe?g)$/i);
        const keys = ctx.keys();
        if (keys && keys.length) {
          const randKey = keys[Math.floor(Math.random() * keys.length)];
          this.bgUrl = ctx(randKey);
          return;
        }
      } catch (e) {
        // require.context may not be available depending on bundler config
      }

      // fallback attempts
      try { this.bgUrl = require('@/assets/bg/0.png'); } catch (e) { this.bgUrl = ''; }
    },

    initPositions() {
      const total = Math.min(this.piecesCount, this.cols * this.rows);
      const ids = Array.from({ length: total }, (_, i) => i);
      this.positions = this.shuffleArray(ids.slice());
    },
    shuffleArray(arr) {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    },
    onDragStart(e, pieceId) {
      this.dragPieceId = pieceId;
      try { e.dataTransfer.setData('text/plain', String(pieceId)); } catch {console.warn('Drag dataTransfer not supported');}
    },
    onDrop(e, toSlot) {
      e.preventDefault();
      let pieceId = null;
      try { pieceId = Number(e.dataTransfer.getData('text/plain')); } catch {console.warn('Drag dataTransfer not supported');}
      if (isNaN(pieceId)) pieceId = this.dragPieceId;
      if (pieceId === null || isNaN(pieceId)) return;

      const fromSlot = this.positions.indexOf(pieceId);
      if (fromSlot === -1 || fromSlot === toSlot) return;

      // Vue 3: use splice for reactivity
      const a = this.positions[fromSlot];
      const b = this.positions[toSlot];
      this.positions.splice(fromSlot, 1, b);
      this.positions.splice(toSlot, 1, a);

      this.$nextTick(() => {
        if (this.checkSolved()) {
          // vollständiges Bild anzeigen und 5 Sekunden stehen lassen
          this.revealed = true;
          this.clearRevealTimer();
          this.revealTimeoutId = setTimeout(() => {
            this.revealed = false;
            this.revealTimeoutId = null;
            this.$emit('done');
          }, 5000);
        }
      });
    },
    pieceStyle(pieceId) {
      const col = pieceId % this.cols;
      const row = Math.floor(pieceId / this.cols);
      const bgSizeX = this.cols * 100;
      const bgSizeY = this.rows * 100;
      const posX = this.cols > 1 ? (col / (this.cols - 1)) * 100 : 0;
      const posY = this.rows > 1 ? (row / (this.rows - 1)) * 100 : 0;

      return {
        backgroundImage: this.bgUrl ? `url(${this.bgUrl})` : 'none',
        backgroundSize: `${bgSizeX}% ${bgSizeY}%`,
        backgroundPosition: `${posX}% ${posY}%`
      };
    },
    checkSolved() {
      for (let i = 0; i < this.positions.length; i++) {
        if (this.positions[i] !== i) return false;
      }
      return true;
    },
    close() {
      this.clearRevealTimer();
      this.$emit('close');
    }
  },
  computed: {
    fullOverlayStyle() {
      return {
        position: 'absolute',
        inset: '0',
        backgroundImage: this.bgUrl ? `url(${this.bgUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        zIndex: 30
      };
    }
  }
};
</script>

<style scoped>
.mini-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.5);
  z-index: 15000;
}
.mini-card {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.4);
  display:flex;
  flex-direction: column;
  align-items: center;
}
.puzzle-card { padding: 10px; }
.mini-header { width:100%; display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; }

.puzzle-area {
  position: relative;
  background: #111;
  border-radius: 6px;
  display: grid;
  grid-template-columns: repeat(var(--cols, 4), 1fr);
  grid-template-rows: repeat(var(--rows, 5), 1fr);
  gap: 2px;
  padding: 2px;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
}
.slot {
  position: relative;
  background: rgba(255,255,255,0.03);
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid rgba(0,0,0,0.25);
  display:flex;
  align-items:center;
  justify-content:center;
  min-width: 0;
  min-height: 0;
}
.slot.solved { box-shadow: inset 0 0 0 3px rgba(34,197,94,0.12); }
.piece {
  width: calc(100% + 1px);
  height: calc(100% + 1px);
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  cursor: grab;
  user-select: none;
}

/* Overlay für das vollständige Bild */
.full-overlay {
  position: absolute;
  inset: 0;
  z-index: 30;
  border-radius: 6px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.4);
  background-size: cover;
  background-position: center;
}

.mini-close { background:transparent;border:0;cursor:pointer;font-size:16px }
.mini-footer { font-size:13px; color:#555; text-align:center; margin-top:8px }
</style>